import clsx from 'clsx';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { getTemplateRule } from '../../data/templateRules';
import templates from '../../data/templates';
import EditorHeader from '../components/EditorHeader';
import EditorKiteSettings from '../components/EditorKiteSettings';
import KiteMeta from '../components/KiteMeta';
import KiteModel from '../components/KiteModel';
import Material from '../components/Material';
import useDraft from '../hooks/useDraft';
import { useEditor } from '../hooks/useEditor';
import useKites from '../hooks/useKites';
import ChevronIcon from '../icons/ChevronIcon';
import NotesIcon from '../icons/NotesIcon';
import PaletteIcon from '../icons/PaletteIcon';
import SettingsIcon from '../icons/SettingsIcon';
import { Editor } from '../services/Store';
import { PartType } from '../types';

enum EditorTool {
    Color = 'color',
    Options = 'options',
    Notes = 'notes',
}

function updateCss(nextSwatch) {
    document.querySelector<HTMLElement>(
        'style[data=current-swatch]'
    ).innerHTML = `
            :root, svg {
                --current-swatch: ${nextSwatch.value};
                --current-swatch-label: '${nextSwatch.label}';
            }`;
}

export default function EditorPage() {
    const { draft, changeNotes } = useDraft();
    const { next, previous, count } = useKites();
    const {
        editor: { swatch, shadow },
        toggleShadow,
    } = useEditor();
    const [tools, setTools] = useState(true);

    const templateRule =
        getTemplateRule(draft.template, draft.variation || null) || null;

    useEffect(() => {
        updateCss(swatch);
    }, [swatch]);

    const [editorTool, setEditorTool] = useState<EditorTool>(EditorTool.Color);
    function handleToolChange(tool: EditorTool) {
        setEditorTool(tool);
    }

    function handlePartChange(part) {
        if (part.type === PartType.Panel) Editor.setPart(part);
    }

    function handleColorChange() {
        if (templateRule && templateRule.globalColorIds)
            Editor.setPartsGlobal(templateRule.globalColorIds);
    }

    function renderTool() {
        switch (editorTool) {
            case EditorTool.Color:
                return (
                    <Material
                        onColorChange={handleColorChange}
                        variations={templates[draft.template].variations}
                    />
                );
            case EditorTool.Options:
                return <EditorKiteSettings />;
            case EditorTool.Notes:
                return (
                    <textarea
                        className="editor__notes"
                        value={(draft.meta && draft.meta.notes) || ''}
                        onChange={({ target: { value } }) =>
                            changeNotes(value, draft.id)
                        }
                    />
                );
        }
    }

    return (
        <>
            <div className="editor__header">
                <EditorHeader />
                <div className="editor__controls">
                    {
                        <div className="kite__actions buttongroup">
                            <button
                                className={clsx('button button--small', {
                                    'is-active':
                                        editorTool === EditorTool.Color,
                                })}
                                onClick={() =>
                                    handleToolChange(EditorTool.Color)
                                }
                                disabled={!tools}
                            >
                                <PaletteIcon />
                                color
                            </button>
                            <button
                                className={clsx('button button--small', {
                                    'is-active':
                                        editorTool === EditorTool.Options,
                                })}
                                onClick={() =>
                                    handleToolChange(EditorTool.Options)
                                }
                                disabled={!tools}
                            >
                                <SettingsIcon />
                                options
                            </button>
                            <button
                                className={clsx('button button--small', {
                                    'is-active':
                                        editorTool === EditorTool.Notes,
                                })}
                                onClick={() =>
                                    handleToolChange(EditorTool.Notes)
                                }
                                disabled={!tools}
                            >
                                <NotesIcon />
                                notes
                            </button>
                        </div>
                    }
                    <div className="kite__actions buttongroup">
                        <button
                            className={clsx('button button--small', {
                                isSelected: !tools,
                            })}
                            onClick={() => setTools(!tools)}
                        >
                            tools on/off
                        </button>
                        <button
                            className={clsx('button button--small', {
                                isSelected: shadow,
                            })}
                            onClick={toggleShadow}
                        >
                            shadow on/off
                        </button>
                    </div>
                </div>
                {tools && (
                    <div className="editor__tools" key={draft.id}>
                        {renderTool()}
                    </div>
                )}
            </div>
            <div className="editor__view">
                <KiteModel
                    className={templateRule && templateRule.className}
                    svg={draft.svg}
                    shadow={shadow}
                    onClick={handlePartChange}
                />
                {count > 1 ? (
                    <div className="editor__view__actions">
                        <button
                            className="button button--small secondary"
                            onClick={previous}
                        >
                            <ChevronIcon deg={180} />
                            previous kite
                        </button>
                        <button
                            className="button button--small secondary"
                            onClick={next}
                        >
                            next kite
                            <ChevronIcon className="end" />
                        </button>
                    </div>
                ) : null}
            </div>
            <div className="editor__meta horizontal-list">
                <KiteMeta templateKey={draft.template} />
            </div>
        </>
    );
}
